import React, { memo, useMemo } from "react";
import { MeatballMenu } from "ui/common/dropdown-menu/meatball-menu";
import { Permission } from "ui/common/permission";

import styles from "./case-table-view-meatball-menu.scss";
import { routeConstants } from "app/routes/route-constants";
import { Link } from 'react-router-dom';

import { caseTableViewPermissionHook } from "app/area/case-report/case-table-view/hooks/case-table-view-permission-hook";
import omit from "lodash/omit";
import { PromptConfirmationModal } from "ui/common/prompt-confirmation/prompt-confirmation-modal";
import { getHostingPath } from "app/infrastructure/hosting";
import { PrintLink } from "app/components/case-header-panel";
import { TranslationType } from "app/area/case-report/case-table-view/case-table-view-container";
import { AddLossPotentialModal } from "app/area/case/components/loss-potential/add-loss-potential-evaluation-modal";
import { AddRiskEvaluationInitialValues } from "app/area/case/features/loss-potential-evaluation/types";
import { withGetUiDefinitionFromEntities } from "app/area/case/features/loss-potential-evaluation";
import { useSelectors } from "app/area/case-report/case-table-view/hooks/case-table-view-hooks";
import { ShareCase, SharePreset, withSharingFromTextPath } from "app/area/case/features";
import { STATE } from "app/constants";
import { isValidUrl } from "app/infrastructure/validation/url";
import { Link as ExternalLink } from 'ui';
import { urlToSurveyManager } from 'app/area/favourite/components/url-to-survey-manager';

interface CaseTableViewMeatballMenuProps {
  caseId: number;
  translation: TranslationType;
  onDeleteCase: (caseId: number) => void;
  onGetUiDefinition?: (caseTypeId: number, caseContextId: number) => void;
  onCopyRiskEvaluation: (caseId: number, riskMatrixInstance: number, instanceCopyCmd: number, 
    initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => void;
  tableViewRef?: React.RefObject<HTMLDivElement>;
  singleSupplierUrl?: string;
}

type PermissionConfig = {
  edit: boolean;
  delete: boolean;
  bowtie: boolean;
  surveyManager: boolean;
  lossPotential: boolean;
  residualLossPotential: boolean;
}

const defaultConfig: PermissionConfig = {
  edit: false,
  delete: false,
  bowtie: false,
  surveyManager: false,
  lossPotential: false,
  residualLossPotential: false
};
function CaseTableViewMeatballMenuMemo(props: CaseTableViewMeatballMenuProps) {
  const { caseId, translation, onDeleteCase, tableViewRef, onCopyRiskEvaluation, singleSupplierUrl } = props;
  const caseURL = caseId ? routeConstants.caseViewFor(caseId) : "";
  const caseEditUrl = caseId ? routeConstants.caseEditFor(caseId) : '';
  const caseDuplicateUrl = caseId ? routeConstants.caseDuplicateFor(caseId) : '';
  const caseBowtieUrl = caseId ? routeConstants.bowtieViewFor(caseId) : '';
  const { entities, access } = useSelectors();

  const { permissions, loadPermissions } = caseTableViewPermissionHook(caseId);  
  const casePermissions = useMemo(() => {
    if (!permissions) { return defaultConfig }
    const omitCaseIDFromPermissions = omit(permissions, 'caseId');
    return {
      ...omitCaseIDFromPermissions
    } as PermissionConfig;
  }, [permissions, defaultConfig]);

  if (!caseId) {
    return null;
  }

  const entitiesForSpecificCaseId = entities && entities.filter(entity=>entity[0].CaseNo[0].value === caseId);
  const AddLossPotentialModalTableView = !!entitiesForSpecificCaseId?.length && 
    withGetUiDefinitionFromEntities(AddLossPotentialModal, 
      Object.values(entitiesForSpecificCaseId[entitiesForSpecificCaseId.length-1])[0]);

  const handleCopyEvaluation = (
    riskMatrixInstance: number, instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean
  ) => {
    onCopyRiskEvaluation?.(caseId, riskMatrixInstance, instanceCopyCmd, initialValues, copyToResidual);
  };
  
  const ShareCaseTableView: any = (withSharingFromTextPath(SharePreset.case, STATE.caseListing, caseId))(ShareCase);

  return (
    <div className={styles.meatballMenuWrapper}>
      <MeatballMenu
        onExpandMenu={() => loadPermissions(caseId)}
        className={styles.meatballMenu}
        portalTarget={tableViewRef?.current}>
        <Permission requireAll="lossPotential" permissions={casePermissions} isListElement={true}>
          <AddLossPotentialModalTableView {...props} 
            textValidFrom={translation.textEvaluationValidFrom}
            textEvaluationDescription={translation.textEvaluationDescription}
            textEvaluationHideInReport={translation.textEvaluationHideInReport}
            textAddEvaluationSubtitle={`${translation.textAddEvaluationSubtitle} ${' #'}${props.caseId}`}
            textAddItem={translation.textAddEvaluationTitle}
            textAdd={translation.textEvaluationAddItem}
            textAddResidualEvaluation={translation.textAddResidualEvaluation}
            textEvaluationCopyFromPreviousResidual={translation.textEvaluationCopyFromPreviousResidual}
            addButtonStyle='dropdownItem'
            allowCopyFromOtherInstance={casePermissions?.residualLossPotential}
            allowCopyEvaluationToResidual={casePermissions?.residualLossPotential}
            onCopyRiskEvaluation={handleCopyEvaluation}/>
        </Permission>
        <Link to={caseURL} className="btn dropdown-item" target="_self">{translation.textOpenCase}</Link>
        <Permission requireAll="edit" permissions={casePermissions}>
          <Link to={caseEditUrl} className="btn dropdown-item" target="_self">{translation.textEditCase}</Link>
        </Permission>
        <Permission requireAll="delete" permissions={casePermissions} isListElement={true}>
          <PromptConfirmationModal
            title={translation.textDeleteCaseTitle}
            description={translation.textDeleteCaseDescription}
            continueText={translation.textDeleteCaseContinue}
            cancelText={translation.textDeleteCaseCancel}
            onContinue={() => onDeleteCase(caseId)}>
            <button type="button" className="dropdown-item">{translation.textDeleteCaseTitle}</button>
          </PromptConfirmationModal>
        </Permission>
        <Permission requireAll="edit" permissions={casePermissions} isListElement={true}>
          <Link to={caseDuplicateUrl} className="btn dropdown-item" target="_self">{translation.textDuplicateCaseTitle}</Link>
        </Permission>
        <Permission requireAll="bowtie" permissions={casePermissions} isListElement={true}>
          <Link to={caseBowtieUrl} className="btn dropdown-item" target="_self">{translation.textOpenBowtieTitle}</Link>
        </Permission>
        <Permission requireAll="edit" permissions={casePermissions} isListElement={true}>
          <ShareCaseTableView {...props}>
            {
              ({ onOpen }) => <button type="button" className="dropdown-item" onClick={onOpen}>{translation.share_case}</button>
            }
          </ShareCaseTableView>        
        </Permission>
        <PrintLink text={translation.textPrintCase} url={(getHostingPath() || '') + caseURL} />
        <Permission requireAll="surveyManager" permissions={casePermissions} isListElement={true}>
          {access?.surveyManager?.send &&
            isValidUrl(urlToSurveyManager(singleSupplierUrl, undefined, caseId)) &&
            <ExternalLink url={urlToSurveyManager(singleSupplierUrl, undefined, caseId)} linkText={translation.textOpenInSurveyManager} 
              className={'dropdown-item'} />}
        </Permission>
      </MeatballMenu>
    </div>
  );
}
export const CaseTableViewMeatballMenu = memo(CaseTableViewMeatballMenuMemo);
